import { useEffect, useRef, useState } from "react";
import { useRecoilState, useRecoilValue } from "recoil";

import { SecurityOverview, securityApi } from "../../backend/security";
import { mfaRequiredModalState } from "../../recoil/mfa-required";
import { currentProjectState, projectsState } from "../../recoil/project";
import { signInAgain } from "../../utils/mfa-step-up";

export type MfaRequiredStep = "required" | "enroll" | "confirm";
type Method = "totp" | "passkey";

/**
 * A project that requires two-step verification refused this session. Without leaving the page:
 * a member with a factor confirms it's them with it; one without adds a factor, then confirms with
 * it. Either way the new session carries two-step verification, so the page reloads with access.
 * Google/GitHub accounts meet the policy through their provider, so only a session from before
 * that needs refreshing; they, and a failed lookup, sign in again.
 */
export function useMfaRequired() {
    const [state, setState] = useRecoilState(mfaRequiredModalState);
    const currentProject = useRecoilValue(currentProjectState);
    const projects = useRecoilValue(projectsState);
    const [overview, setOverview] = useState<SecurityOverview>();
    const [overviewFailed, setOverviewFailed] = useState(false);
    const [step, setStep] = useState<MfaRequiredStep>("required");
    const [method, setMethod] = useState<Method>();
    const enrolled = useRef(false);

    useEffect(() => {
        if (!state.visible) {
            return;
        }
        setStep("required");
        setMethod(undefined);
        setOverview(undefined);
        setOverviewFailed(false);
        enrolled.current = false;
        securityApi.getOverview().then(setOverview).catch(() => setOverviewFailed(true));
    }, [state.visible]);

    const projectName = (state.projectId
        ? projects.find(project => project.id === state.projectId)?.name
        : undefined) || (state.projectId && state.projectId === currentProject?.id ? currentProject?.name : undefined);

    const close = () => setState({ visible: false });

    return {
        visible: state.visible,
        step,
        method,
        projectName,
        overview,
        loading: state.visible && !overview && !overviewFailed,
        /** Signing in again is the only way through: Google/GitHub accounts, or the account couldn't be read */
        mustSignIn: overviewFailed || !!overview?.federated,
        federated: !!overview?.federated,
        hasFactors: !!overview?.authenticationFactors.length,
        close,
        enroll: (next: Method) => { setMethod(next); setStep("enroll"); },
        enrollmentComplete: () => { enrolled.current = true; },
        enrollmentClosed: () => { setMethod(undefined); setStep(enrolled.current ? "confirm" : "required"); },
        confirm: () => setStep("confirm"),
        // The refused calls aren't replayed one by one; a reload asks again with the new session
        confirmed: () => { close(); window.location.reload(); },
        signIn: () => signInAgain(`${window.location.pathname}${window.location.search}`),
    };
}
